import {
  ChevronLeft,
  ChevronRight,
  FileText,
  LoaderCircle,
  Plus,
  RotateCcw,
} from 'lucide-react';
import type { AsyncState, SessionSummary } from '../api';
import { formatDate } from '../utils';

interface SessionRailProps {
  collapsed: boolean;
  sessions: SessionSummary[];
  sessionsState: AsyncState;
  selectedSessionId?: string;
  error?: string;
  onCollapse(): void;
  onNewSession(): void;
  onSelect(sessionId: string): void;
  onResume(session: SessionSummary): void;
}

export function SessionRail({
  collapsed,
  sessions,
  sessionsState,
  selectedSessionId,
  error,
  onCollapse,
  onNewSession,
  onSelect,
  onResume,
}: SessionRailProps) {
  return (
    <aside className={`session-rail ${collapsed ? 'is-collapsed' : ''}`} aria-label="会话">
      <div className="rail-actions">
        <button className="primary-button rail-new" type="button" onClick={onNewSession}>
          <Plus size={16} aria-hidden="true" />
          {!collapsed && <span>新建会话</span>}
        </button>
        <button
          className="icon-button"
          type="button"
          onClick={onCollapse}
          aria-label={collapsed ? '展开会话栏' : '收起会话栏'}
        >
          {collapsed ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
        </button>
      </div>

      {!collapsed && <div className="rail-section-title">历史会话</div>}

      <div className="session-list">
        {sessionsState === 'loading' && (
          <div className="rail-state">
            <LoaderCircle className="spin" size={17} />
            {!collapsed && <span>正在读取会话</span>}
          </div>
        )}
        {sessionsState === 'error' && !collapsed && (
          <div className="rail-message error-message">{error ?? '会话列表加载失败。'}</div>
        )}
        {sessionsState === 'ready' && sessions.length === 0 && !collapsed && (
          <div className="rail-message">还没有历史会话。</div>
        )}

        {sessions.map((session) => {
          const active = selectedSessionId === session.id;
          return (
            <div className={`session-row ${active ? 'is-active' : ''}`} key={session.id}>
              <button
                type="button"
                className="session-main"
                onClick={() => onSelect(session.id)}
                aria-current={active ? 'page' : undefined}
                title={collapsed ? session.title : undefined}
              >
                <span className="session-icon" aria-hidden="true">
                  <FileText size={16} />
                  {session.running && <span className="running-dot" />}
                </span>
                {!collapsed && (
                  <span className="session-copy">
                    <span className="session-title">{session.title}</span>
                    <span className="session-meta">
                      {session.running ? '执行中' : session.status || '历史'}
                      {session.updatedAt ? ` · ${formatDate(session.updatedAt)}` : ''}
                    </span>
                  </span>
                )}
              </button>
              {!collapsed && session.resumable && !session.running && (
                <button
                  className="session-resume"
                  type="button"
                  onClick={() => onResume(session)}
                  aria-label={`继续 ${session.title}`}
                  title="继续会话"
                >
                  <RotateCcw size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
